import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  useTheme,
  TablePagination,
} from '@mui/material';
import { Add, Edit, Delete, EventBusy } from '@mui/icons-material';
import { nptApi } from '../../services/api';
import { Meta, NPTReason } from '../../types'; 
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { unwrapListResponse, unwrapMetaResponse } from '../../apiUnwrapper/apiUnwrapper';
import NPTReasonFormModalProps from '../../components/AddNewNPTReasonPopUp';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useSnackbar } from 'notistack';
import {
  PageHeader,
} from '../../components/SharedPageParts';
import { useAuth } from '../../services/AuthContext';

const NPTReasonsPage: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canManageNPTReasons = user?.roleName === 'Admin';

    const [nptReasons, setNPTReasonsList] = useState<NPTReason[]>([]);
    const [nptReasonsMeta, setNPTReasonsMeta] = useState<Meta>({ total: 0, size: 5, page: 1 });
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const [selectedNPTReason, setSelectedNPTReason] = useState<NPTReason | undefined>(undefined); // equipment to edit
    const [isEditMode, setIsEditMode] = useState(false);
    const [openConfirm, setOpenConfirm] = useState(false);
    const [confirmAction, setConfirmAction] = useState<() => void>(() => () => {});
    const [confirmMessage, setConfirmMessage] = useState("Are you sure?");
    const { enqueueSnackbar } = useSnackbar();

    useEffect(() => {
        loadNPTReasons(nptReasonsMeta.page, nptReasonsMeta.size);
      }, []);

    const loadNPTReasons = async (page: number, size: number) => {
          try {
            const data = await nptApi.getAllNPTReasons(page, size);
            setError(null);
            setNPTReasonsList(unwrapListResponse<NPTReason>(data));
            setNPTReasonsMeta(unwrapMetaResponse<any>(data));
          } catch (err) {
            setError('Error loading Non Productive Time reasons: '+err );
            console.error('Error loading Non Productive Time reasons:', err);
          }
    };

    const handleSubmit = async (nptReason: NPTReason) => {
            try{
              const result = await nptApi.createUpdateNPTReason(nptReason);
              if(isEditMode)
                enqueueSnackbar('NPT Reason updated successfully', { variant: 'success' });
              else
                enqueueSnackbar('NPT Reason created successfully', { variant: 'success' });
              loadNPTReasons(nptReasonsMeta.page, nptReasonsMeta.size);
              setOpen(false);
            }
            catch(error){
              setError('Error creating Non Productive Time reasons: '+error );
              console.error('Error creating Non Productive Time reasons: '+error);
              enqueueSnackbar('Error creating Non Productive Time reasons: '+error, { variant: 'error' });
            }
          };

    const handleDelete = async (id: any) => {
        try{
              setConfirmAction(() => async () => {
              await nptApi.deleteNPTResponse(id);
              loadNPTReasons(nptReasonsMeta.page, nptReasonsMeta.size);
              enqueueSnackbar('NPT Reason deleted successfully', { variant: 'success' });
              });
              setConfirmMessage("Are you sure you want to delete this NPT Reason?");
              setOpenConfirm(true);
            }
            catch(error){
              setError('Error deleting Non Productive Time reasons: '+error );
              console.error('Error deleting Non Productive Time reasons: '+error);
              enqueueSnackbar('Error deleting Non Productive Time reasons: '+error, { variant: 'error' });
            }
    };

    return (
        <Box sx={{ minHeight: '100vh', backgroundColor: theme.palette.background.default }}>
            <Box className="MuiBox-root-margin" sx={{ px: 1, mx: 'auto' }}>
            <PageHeader
                                    title="Non Productive Time Reasons"
                                    Icon={EventBusy}
                                    onBack={() => navigate('/prechecksandforms')}
                                    backLabel="Back to Pre Checks and Forms"
                                    subtitle=""
                                />

                <Card sx={{ backgroundColor: theme.palette.background.paper, borderRadius: 2, mb: 3 }}>
                    <CardContent>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                        {canManageNPTReasons && <>
                        <Button
                            variant="contained"
                            startIcon={<Add />}
                            sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #4c5fd7 100%)',
                                        '&:hover': { background: 'linear-gradient(135deg, #4c5fd7 0%, #c2410c 100%)' },
                                }}
                            onClick={() => {
                                setSelectedNPTReason(undefined);
                                setIsEditMode(false);
                                setOpen(true);
                            }}
                            >
                            Add NPT Reason
                        </Button>
                        <NPTReasonFormModalProps open={open} onClose={() => setOpen(false)} onSubmit={handleSubmit}
                        selectedNPTReason={selectedNPTReason} 
                        isEditMode={isEditMode}/>
                        </>}
                        <TablePagination
                            component="div"
                            count={nptReasonsMeta.total}
                            page={nptReasonsMeta.page - 1}
                            onPageChange={(e, newPage) => loadNPTReasons(newPage + 1, nptReasonsMeta.size)}
                            rowsPerPage={nptReasonsMeta.size}
                            onRowsPerPageChange={(e) => loadNPTReasons(1, parseInt(e.target.value, 10))}
                            rowsPerPageOptions={[5, 10, 15, 20]}
                            labelRowsPerPage="Reasons per page"
                            />
                        </Box>
                        <TableContainer
                                      component={Paper}
                                      sx={{ backgroundColor: theme.palette.background.default, borderColor: theme.palette.divider }}>
                            <Table>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#2d3e50' }}>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>Order</TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>Code</TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>Name</TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>Description</TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>Staus</TableCell>
                                        {canManageNPTReasons && <TableCell sx={{ color: theme.palette.text.primary }} align="center">
                                            Actions
                                        </TableCell>}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {nptReasons.map((npt) => (
                                        <TableRow key={npt.id}>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>{npt.sortOrder}</TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>{npt.code}</TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>{npt.name}</TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>{npt.description}</TableCell>
                                            <TableCell sx={{ color: theme.palette.text.primary }}>
                                                { npt.isActive? (
                                                    <Chip label='Active' size="small" />
                                                ) : (
                                                    <Chip label='Inactive' size="small" />
                                                    )
                                                }
                                            </TableCell>
                                            {canManageNPTReasons && <TableCell align="center" sx={{ borderBottom: 'none' }}>
                                                <IconButton size="small" sx={{ color: '#06beb6' }}
                                                onClick={() => {
                                                    setSelectedNPTReason(npt);
                                                    setIsEditMode(true);
                                                    setOpen(true);
                                                    }}>
                                                <Edit />
                                                </IconButton>
                                                    <IconButton
                                                    key={npt.id}
                                                    size="small"
                                                    sx={{ color: '#ef4444' }}
                                                    onClick={() => handleDelete(npt.id)} 
                                                    >
                                                    <Delete />
                                                </IconButton>
                                                <ConfirmDialog
                                                    open={openConfirm}
                                                    onClose={() => setOpenConfirm(false)}
                                                    onConfirm={confirmAction}
                                                    title="Confirm Delete"
                                                    message={confirmMessage}
                                                    />
                                            </TableCell>}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <TablePagination
                            component="div"
                            count={nptReasonsMeta.total}
                            page={nptReasonsMeta.page - 1}
                            onPageChange={(e, newPage) => loadNPTReasons(newPage + 1, nptReasonsMeta.size)}
                            rowsPerPage={nptReasonsMeta.size}
                            onRowsPerPageChange={(e) => loadNPTReasons(1, parseInt(e.target.value, 10))}
                            rowsPerPageOptions={[5, 10, 15, 20]}
                            labelRowsPerPage="Reasons per page"
                            />
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );

};
export default NPTReasonsPage;